/* eslint-disable unicorn/consistent-function-scoping */
import FileSaver from 'file-saver';
import moment from 'moment';
import Axios, { AxiosRequestConfig } from 'axios';
import { ShortMessagePostRequest } from '../../../entities/short-message-request-configuration-v1/short-message-post-request';
import ConfigurationFiles from '../../../services/config-files';
import ConfigurationFilesLibrary from '../../../services/config-files-library';
import FileStorage from '../../../services/file-storage';
import FileTypeConfiguration from '../../../services/file-type-configurations';
import ShortMessageRequestConfiguration from '../../../services/short-message-request-configuration';
import {
  CompleteUpload,
  getConfigFilesRequestBody,
  PushConfigFilesRequestBody,
  RegisterArguments,
} from '../../../services/types';

import { AppState, AppThunkDispatch } from '../..';
import { enqueueSnackbarAction } from '../../snackbar/actions';
import { waitForSuccessfulQueueThenMessage } from '../general-commands/actions';
import {
  createChunks,
  fileToBase64,
  parametersAsObject,
  fileContentsToBlob,
} from '../../../globals/utils';
import { RemoveConfigFilesRequest } from '../../../entities/config-files-v1/remove-config-files-request';

export const getFileTypeOptions =
  () => async (_dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: {
        client,
        envVariables: { fileTypeConfigV2BaseUrl },
      },
    } = getState();
    const response = await new FileTypeConfiguration(
      client,
      fileTypeConfigV2BaseUrl,
    ).getFileTypeConfigurationRecords({ classification: 'config' });
    return response.data.fileTypeConfigs;
  };

export const getRetrievableFileTypes =
  () => async (_dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: {
        client,
        envVariables: { configurationFilesLibraryV1BaseUrl },
      },
    } = getState();
    const input: getConfigFilesRequestBody = { isRetrievable: true };
    const response = await new ConfigurationFilesLibrary(
      client,
      configurationFilesLibraryV1BaseUrl,
    ).getConfigFiles(input);
    return response.data;
  };

export const shortMessageRequestConfig =
  ({ fileTypeId }: { fileTypeId: string }) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      search: { selectedEntity },
      login: {
        client,
        envVariables: { shortMessageRequestConfigurationV1BaseUrl },
      },
    } = getState();

    const deviceKey = selectedEntity?.deviceKey;
    if (selectedEntity && deviceKey) {
      const input: ShortMessagePostRequest = {
        configs: [
          {
            deviceKey,
            fileTypeId,
          },
        ],
      };
      const shortMessageRequestConfiguration = new ShortMessageRequestConfiguration(
        client,
        shortMessageRequestConfigurationV1BaseUrl,
      );
      try {
        const response = await shortMessageRequestConfiguration.queueShortMessageForAssetOrDevice(
          input,
        );
        const { transactionId } = response.data.configs[0];
        dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
      } catch {
        dispatch(
          enqueueSnackbarAction({
            message: 'Request Failure',
            options: {
              variant: 'error',
            },
          }),
        );
        return 'Request Failure';
      }
    }
    return 'Request Failure';
  };

const uploadSmall =
  (file: File, fileTypeId: string, partNumber: string) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        search: { selectedEntity },
        login: {
          client,
          envVariables: { configurationFilesV1BaseUrl, configurationFilesLibraryV1BaseUrl },
        },
      } = getState();

      const deviceKey = selectedEntity?.deviceKey;
      const deviceId = selectedEntity?.deviceId;
      const deviceType = selectedEntity?.deviceType;

      const file64: string | unknown = file && (await fileToBase64(file));
      if (deviceKey && deviceId && deviceType && typeof file64 === 'string') {
        const configFile = file64.replace(/(^[^,]*,)/g, '');
        const schedulerFileTypes = new Set(['10516', '10556', '10086']);

        let input: PushConfigFilesRequestBody;
        if (schedulerFileTypes.has(fileTypeId)) {
          input = {
            configs: [
              {
                deviceId,
                deviceType,
                configFile: {
                  fileTypeId,
                  configFile,
                },
              },
            ],
            metadata: {
              isRetrievable: true,
              fileTypeId,
              fileName: file.name,
              applicationId: 'iot-gateway',
              partNumber,
            },
          };
        } else {
          input = {
            configs: [
              {
                deviceId,
                deviceType,
                configFile: {
                  fileTypeId,
                  configFile,
                },
              },
            ],
            metadata: {
              isRetrievable: false,
              fileTypeId,
              fileName: file.name,
              applicationId: 'iot-gateway',
              partNumber,
            },
          }
        }
        const commands = new ConfigurationFiles(client, configurationFilesV1BaseUrl);
        const configLibrary = new ConfigurationFilesLibrary(
          client,
          configurationFilesLibraryV1BaseUrl,
        );
        try {
          const fileBlob: Blob | unknown = file && (await fileContentsToBlob(file));
          if (fileBlob instanceof Blob) {
            const formData = new FormData();
            formData.append('file', fileBlob, file.name);
            formData.append('metadata', JSON.stringify(input.metadata));
            if (schedulerFileTypes.has(fileTypeId)) formData.append('overwrite', 'false');
            else formData.append('overwrite', 'true');
            await configLibrary.uploadConfigFiles(formData);
          }
        } catch (error: any) {
          if (error.response.status === 409) {
            const hash = error?.response?.data?.id;
            input = {
              configs: [
                {
                  deviceId,
                  deviceType,
                  configFile: {
                    fileTypeId,
                    hash,
                  },
                },
              ],
              metadata: {
                isRetrievable: true,
                fileTypeId,
                fileName: file.name,
                applicationId: 'iot-gateway',
                partNumber,
              },
            };
            if (hash) {
              const response = await commands.pushConfigFiles(input);
              const { description, transactionId } = response.data.configs[0];
              dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
              return description;
            }
          }
          dispatch(
            enqueueSnackbarAction({
              message: 'Request Failure',
              options: {
                variant: 'error',
              },
            }),
          );

          return null;
        }
        try {
          const response = await commands.pushConfigFiles(input);
          const { description, transactionId } = response.data.configs[0];
          dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
          return description;
        } catch (error) {
          dispatch(
            enqueueSnackbarAction({
              message: 'Request Failure',
              options: {
                variant: 'error',
              },
            }),
          );
          return null;
        }
      }
      return null;
    };

const uploadBatch =
  (partNumber: string, file: File, fileTypeId: string, setPercentUpload: Function) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      search: { selectedEntity },
      login: {
        client,
        envVariables: {
          storageV1BaseUrl,
          configurationFilesLibraryV1BaseUrl,
          configurationFilesV1BaseUrl,
        },
      },
    } = getState();

    const storage = new FileStorage(client, storageV1BaseUrl);
    const chunks = createChunks(file, 10);
    const numberOfParts = chunks.length;
    const partyNumber = 'CAT';
    const deviceKey = selectedEntity?.deviceKey;
    const deviceId = selectedEntity?.deviceId;
    const deviceType = selectedEntity?.deviceType;
    try {
      if (deviceId && deviceType && deviceKey) {
        const registrationInputs: RegisterArguments = {
          queryParameters: {
            partNumber,
            numberOfParts,
            partyNumber,
            fileName: file.name,
            origin: 'iot-gateway',
            ttl: 86400,
            contentType: 'application/bin',
          },
        };

        let percentUpload = 10;
        setPercentUpload(percentUpload);
        const {
          data: { uploadUrls, id, uploadId },
        } = await storage.multiPartRegister(registrationInputs);

        const uploadMultiple = () =>
          uploadUrls.map(async (uploadUrl, iterator) => {
            const uploadPercentage = 80 / uploadUrls.length;
            const url = uploadUrl.split('?');
            const parameterString: URLSearchParams | any = new URLSearchParams(url[1]);
            const urlParameters = parametersAsObject(parameterString);
            const request: AxiosRequestConfig = {
              url: url[0],
              params: urlParameters,
              method: 'PUT',
              data: chunks[iterator],
              headers: {
                'Content-Type': 'application/octet-stream',
              },
            };
            const uploadResponse = await Axios(request);
            percentUpload += uploadPercentage;
            setPercentUpload(percentUpload);
            return uploadResponse;
          });

        await Promise.all(uploadMultiple());

        const completionInput: CompleteUpload = {
          queryParameters: {
            partyNumber,
            uploadId,
            id,
          },
        };

        const {
          data,
          data: { fileName },
        } = await storage.multiPartCompleteUpload(completionInput);
        percentUpload += 10;
        setPercentUpload(percentUpload);
        const clientAuth: any = client?.authenticate();
        const userId = clientAuth.loginId;
        const parameters = new URLSearchParams();
        parameters.append('fileId', data.id);
        parameters.append(
          'metadata',
          JSON.stringify({
            isRetrievable: false,
            fileTypeId,
            fileName,
            userId,
            partNumber,
            applicationId: 'iot-gateway',
          }),
        );

        const configure = new ConfigurationFilesLibrary(client, configurationFilesLibraryV1BaseUrl);
        const finish = await configure.uploadConfigFilesFromStorage(parameters);
        percentUpload += 10;
        const input: PushConfigFilesRequestBody = {
          configs: [
            {
              deviceId,
              deviceType,
              configFile: {
                fileTypeId,
                hash: finish.data.id,
              },
            },
          ],
        };
        const commands = new ConfigurationFiles(client, configurationFilesV1BaseUrl);
        const response = await commands.pushConfigFiles(input);
        const { transactionId } = response.data.configs[0];
        dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
        setPercentUpload(0);
        return finish;
      }
    } catch {
      setPercentUpload(0);
      dispatch(
        enqueueSnackbarAction({
          message: 'There was a upload error please try again',
          options: {
            variant: 'error',
          },
        }),
      );
      return null;
    }
    return null;
  };

export const getAllConfigFileStatuses =
  () => async (_dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      search: { selectedEntity },
      login: {
        client,
        envVariables: { configurationFilesV1BaseUrl },
      },
    } = getState();

    const deviceKey = selectedEntity?.deviceKey;
    const deviceId = selectedEntity?.deviceId;
    const deviceType = selectedEntity?.deviceType;
    if (deviceKey && deviceId && deviceType && selectedEntity) {
      const commands = new ConfigurationFiles(client, configurationFilesV1BaseUrl);
      try {
        const response = await commands.getConfigurationFileStatuses({
          deviceKey,
        });
        const { configs } = response.data;
        if (configs.length > 0) {
          configs.forEach((c) => {
            if (c.commands.length > 0) {
              const sortCommand = c.commands.sort((a, b) => {
                const momentA = moment(a.lastUpdatedTime);
                const momentB = moment(b.lastUpdatedTime);
                return momentA.diff(momentB);
              });
              // eslint-disable-next-line no-param-reassign
              c.upload = sortCommand[0].lastUpdatedTime;
            } else {
              // eslint-disable-next-line no-param-reassign
              c.upload = '';
            }
          });
          return configs;
        }
        return configs;
      } catch (error) {
        return [];
      }
    }
    return [];
  };

export const pushConfigFile = ({
  file,
  fileTypeId,
  setPercentUpload,
  partNumber,
}: {
  file: File;
  fileTypeId: string;
  setPercentUpload: Function;
  partNumber: string;
}) => {
  if (file.size < 5000000) return uploadSmall(file, fileTypeId, partNumber);
  return uploadBatch(partNumber, file, fileTypeId, setPercentUpload);
};

export const downloadConfigFile =
  ({
    fileTypeId,
    hash,
    fileName,
  }: {
    fileTypeId: string;
    hash: string;
    fileName: string | null;
  }) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: {
        client,
        envVariables: { configurationFilesLibraryV1BaseUrl },
      },
    } = getState();
    const commands = new ConfigurationFilesLibrary(client, configurationFilesLibraryV1BaseUrl);
    try {
      const response = await commands.downloadConfigFile(hash);
      dispatch(
        enqueueSnackbarAction({
          message: 'Success',
          options: {
            variant: 'success',
          },
        }),
      );
      if (fileName) FileSaver.saveAs(response.data, fileName);
      else FileSaver.saveAs(response.data, `${fileTypeId}`);
      return response;
    } catch (error) {
      dispatch(
        enqueueSnackbarAction({
          message: 'Download Failure',
          options: {
            variant: 'error',
          },
        }),
      );
      return 'Download Failure';
    }
  };

export const deleteConfigFiles =
  (filesToDelete: RemoveConfigFilesRequest) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: {
        client,
        envVariables: { configurationFilesV1BaseUrl },
      },
    } = getState();
    const configFiles = new ConfigurationFilesLibrary(client, configurationFilesV1BaseUrl);
    try {
      await configFiles.removeConfigFiles(filesToDelete);
      dispatch(
        enqueueSnackbarAction({
          message: 'File successfully deleted',
          options: {
            variant: 'success',
          },
        }),
      );
      return true;
    } catch (error) {
      dispatch(
        enqueueSnackbarAction({
          message: 'There was an error deleting the file',
          options: {
            variant: 'error',
          },
        }),
      );
      return false;
    }
  };
